<script>
	import { onMount, onDestroy } from 'svelte';
	import Chart from 'chart.js/auto';
	import 'chartjs-adapter-date-fns';
	import { STAGE_COLORS, STAGE_Y_VALUE, STAGE } from './DataParser.js';
	import { baseChartOptions, timeXAxis, darkTooltip, COLORS, registerTimeNearestMode } from './ChartTheme.js';

	export let ourData = [];
	export let samsungData = [];
	export let title = '수면 단계 비교';

	let chartCanvas;
	let chart;

	const stageYLabels = { 1: 'Deep', 2: 'Light', 3: 'REM', 4: 'Awake', 5: '신호없음' };

	function getSegmentColor(ctx, data) {
		const index = ctx.p0DataIndex;
		if (index < 0 || index >= data.length) return '#cccccc';
		const stage = data[index]?.stage;
		return STAGE_COLORS[stage] || '#cccccc';
	}

	function buildDataset(label, data, borderDash, isSamsung) {
		return {
			label,
			data: data.map(d => ({ x: d.time, y: d.yValue })),
			stepped: 'after',
			borderWidth: isSamsung ? 1.5 : 2.5,
			pointRadius: 0,
			pointHoverRadius: 4,
			fill: isSamsung ? false : true,
			borderDash: borderDash || [],
			segment: {
				borderColor: ctx => getSegmentColor(ctx, data),
				backgroundColor: ctx => {
					if (isSamsung) return 'transparent';
					const color = getSegmentColor(ctx, data);
					const r = parseInt(color.slice(1, 3), 16);
					const g = parseInt(color.slice(3, 5), 16);
					const b = parseInt(color.slice(5, 7), 16);
					return `rgba(${r}, ${g}, ${b}, 0.12)`;
				},
			},
		};
	}

	function createChart() {
		registerTimeNearestMode();
		if (chart) chart.destroy();
		if (!chartCanvas) return;
		if (ourData.length === 0 && samsungData.length === 0) return;

		const datasets = [];
		if (ourData.length > 0) {
			datasets.push(buildDataset('Sleepwave', ourData, [], false));
		}
		if (samsungData.length > 0) {
			datasets.push(buildDataset('Samsung Health', samsungData, [6, 4], true));
		}

		chart = new Chart(chartCanvas, {
			type: 'line',
			data: { datasets },
			options: {
				...baseChartOptions(),
				layout: { padding: { top: 4, right: 8, bottom: 0, left: 0 } },
				plugins: {
					legend: {
						display: datasets.length > 1,
						position: 'top',
						align: 'end',
						labels: {
							color: COLORS.textSecondary,
							font: { size: 11 },
							boxWidth: 12,
							boxHeight: 8,
							padding: 16,
							borderRadius: 4,
						}
					},
					tooltip: {
						...darkTooltip,
						callbacks: {
							label: function(ctx) {
								const yVal = ctx.parsed.y;
								const label = stageYLabels[yVal] || 'Unknown';
								return `${ctx.dataset.label}: ${label}`;
							},
							title: function(items) {
								if (items.length > 0) {
									return new Date(items[0].parsed.x).toLocaleTimeString('ko-KR', {
										hour: '2-digit', minute: '2-digit', second: '2-digit'
									});
								}
								return '';
							},
							labelColor: function(ctx) {
								const data = ctx.dataset.label === 'Sleepwave' ? ourData : samsungData;
								const idx = ctx.dataIndex;
								const stage = idx < data.length ? data[idx]?.stage : null;
								const color = STAGE_COLORS[stage] || '#818cf8';
								return { borderColor: color, backgroundColor: color, borderWidth: 2, borderRadius: 2 };
							}
						}
					}
				},
				scales: {
					x: timeXAxis(),
					y: {
						min: 0.5,
						max: 5.5,
						reverse: false,
						ticks: {
						display: true,
						callback: function(value) {
							const labels = { 1: 'Deep', 2: 'Light', 3: 'REM', 4: 'Awake', 5: '신호없음' };
							return labels[value] || '';
						},
						color: function(context) {
							const stageColors = {
								1: STAGE_COLORS[STAGE.DEEP],
								2: STAGE_COLORS[STAGE.LIGHT],
								3: STAGE_COLORS[STAGE.REM],
								4: STAGE_COLORS[STAGE.AWAKE_IN_BED],
								5: STAGE_COLORS[STAGE.INVALID],
							};
							return stageColors[context.tick.value] || '#94a3b8';
						},
						font: { size: 12, weight: '600' },
						padding: 8,
					},
						grid: {
							color: 'rgba(226, 232, 240, 0.35)',
							drawBorder: false,
							tickLength: 0,
						},
						border: { display: false },
						afterBuildTicks: (axis) => {
							axis.ticks = [1, 2, 3, 4, 5].map(v => ({ value: v }));
						},
					}
				}
			}
		});
	}

	$: if (chartCanvas && (ourData || samsungData)) {
		createChart();
	}

	onMount(() => {
		if (ourData.length > 0 || samsungData.length > 0) {
			createChart();
		}
	});

	onDestroy(() => {
		if (chart) chart.destroy();
	});
</script>

<div class="chart-card">
	<div class="chart-header">
		<h5 class="chart-title">{title}</h5>
	</div>
	<div class="chart-body">
		{#if ourData.length === 0 && samsungData.length === 0}
			<div class="empty-state">데이터가 없습니다</div>
		{:else}
			<div class="chart-container">
				<canvas bind:this={chartCanvas}></canvas>
			</div>
		{/if}
	</div>
</div>

<style>
	.chart-card {
		background: #ffffff;
		border: 1px solid #e2e8f0;
		border-radius: 16px;
		padding: 20px 24px 16px;
		margin-bottom: 20px;
		box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
	}
	.chart-header {
		margin-bottom: 12px;
	}
	.chart-title {
		font-size: 15px;
		font-weight: 600;
		color: #334155;
		margin: 0;
	}
	.chart-body {
		position: relative;
	}
	.chart-container {
		height: 260px;
	}
	.empty-state {
		text-align: center;
		color: #94a3b8;
		padding: 48px 0;
		font-size: 14px;
	}
</style>
